import React, { useState, useEffect } from "react";
import {
  Table,
  Modal,
  Form,
  Input,
  Select,
  Button,
  Tag,
  Popconfirm,
  Empty,
  Spin,
  message,
  Typography,
  Space,
  Divider,
} from "antd";
import {
  ReloadOutlined,
  DeleteOutlined,
  BankOutlined,
  CheckCircleFilled,
  ClockCircleFilled,
  CloseCircleFilled,
} from "@ant-design/icons";
import axios from "axios";
import { useStripe, useElements } from "@stripe/react-stripe-js";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const BASE_URL = import.meta.env.VITE_APP_BASE_URL;

const BankAccountManager = ({ role, user, headerJson, searchParams, setSearchParams }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);

  // Verification flow state for Tenants
  const [verificationModalVisible, setVerificationModalVisible] = useState(false);
  const [pendingAccount, setPendingAccount] = useState(null);
  const [savingAccount, setSavingAccount] = useState(false);

  // Roles that receive money use the Stripe Connect payout flow (home_owner, cleaning_company, property_manager)
  const isPayoutRole = role === "homeowner";

  useEffect(() => {
    if (user?.token) {
      fetchData();
    }
  }, [user?.token, role]);

  // Payout onboarding success logic (URL-based) — for home_owner, cleaning_company, property_manager
  useEffect(() => {
    if (isPayoutRole && searchParams?.get("success") === "1" && user?.token) {
      handleOnboardingSuccess();
    }
  }, [searchParams, user?.token, isPayoutRole]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const url = isPayoutRole
        ? `${BASE_URL}/api/payout/list`
        : `${BASE_URL}/api/payment-method/get-bank-accounts`;

      const res = await axios.get(url, { headers: headerJson });
      if (res.data?.success || res.data?.statusCode === 200) {
        setData(res.data.data || []);
        return res.data.data || [];
      }
    } catch (error) {
      console.error("Error fetching bank accounts:", error);
      message.error("Failed to load bank accounts");
    } finally {
      setLoading(false);
    }
  };

  const handleOnboardingSuccess = async () => {
    try {
      const accounts = await fetchData();
      if (accounts && accounts.length > 0) {
        const mostRecent = accounts[0];
        message.loading({ content: "Finalizing bank account setup...", key: "payout-refresh" });
        await refreshPayoutStatus(mostRecent.id);
        message.success({ content: "Bank account setup successful!", key: "payout-refresh" });
      }

      searchParams.delete("success");
      setSearchParams(searchParams);
    } catch (error) {
      console.error("Error finalizing onboarding:", error);
    }
  };

  const refreshPayoutStatus = async (id) => {
    setRefreshingId(id);
    try {
      await axios.post(`${BASE_URL}/api/payout/refresh-status/${id}`, {}, {
        headers: headerJson,
      });
      message.success("Payout status updated");
      fetchData();
    } catch (error) {
      message.error("Failed to refresh status");
    } finally {
      setRefreshingId(null);
    }
  };

  const handleDelete = async (id) => {
    try {
      const url = isPayoutRole
        ? `${BASE_URL}/api/payout/delete/${id}`
        : `${BASE_URL}/api/payment-method/delete-bank-account/${id}`;

      await axios.delete(url, { headers: headerJson });
      message.success("Bank account removed");
      fetchData();
    } catch (error) {
      message.error("Failed to remove bank account");
    }
  };

  const saveBankAccountToBackend = async (payload) => {
    if (!payload) return;
    setSavingAccount(true);
    try {
      await axios.post(
        `${BASE_URL}/api/payment-method/save-bank-account`,
        payload,
        { headers: headerJson }
      );
      message.success("Bank account added successfully");
      setModalVisible(false);
      setVerificationModalVisible(false);
      setPendingAccount(null);
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to save bank account");
    } finally {
      setSavingAccount(false);
    }
  };

  const onFinish = async (values) => {
    setSubmitting(true);
    try {
      if (isPayoutRole) {
        // Payout receiver (home_owner, cleaning_company, property_manager): Connect Payout (Redirect to Stripe)
        const res = await axios.post(`${BASE_URL}/api/payout/add-bank-account`, values, {
          headers: headerJson,
        });
        if (res.data?.data?.onboarding_url) {
          message.loading("Redirecting to Stripe for secure setup...", 2);
          window.location.href = res.data.data.onboarding_url;
        }
      } else {
        // Tenant / Relo: Setup ACH Bank Account (SetupIntent Flow)
        if (!stripe || !elements) {
          message.error("Stripe has not loaded yet.");
          return;
        }

        // 1. Create SetupIntent
        const intentRes = await axios.post(
          `${BASE_URL}/api/payment-method/create-bank-setup-intent`,
          {},
          { headers: headerJson }
        );
        const { client_secret } = intentRes.data.data;

        // 2. Confirm Bank Account Setup
        const { setupIntent, error } = await stripe.confirmUsBankAccountSetup(client_secret, {
          payment_method: {
            billing_details: {
              name: values.account_holder_name,
              email: user.email,
            },
            us_bank_account: {
              routing_number: values.routing_number,
              account_number: values.account_number,
              account_holder_type: values.account_type === "business" ? "company" : "individual",
            },
          },
        });

        if (error) {
          message.error(error.message);
          return;
        }

        // Store data in state for "Finish Setup"
        const accountData = {
          payment_method_id: setupIntent.payment_method,
          account_holder_name: values.account_holder_name,
          account_type: values.account_type,
        };
        setPendingAccount(accountData);

        if (setupIntent.status === "requires_action") {
          // Handle microdeposit verification via New Tab
          const hostedUrl = setupIntent.next_action?.verify_with_microdeposits?.hosted_verification_url;
          if (hostedUrl) {
            window.open(hostedUrl, "_blank");
            setVerificationModalVisible(true);
          } else {
            message.error("Verification required but no URL provided.");
          }
          return;
        }

        if (setupIntent.status === "succeeded" || setupIntent.status === "processing") {
          // Save to backend immediately if no extra action needed
          await saveBankAccountToBackend(accountData);
        }
      }
    } catch (error) {
      console.error("Setup error:", error);
      message.error(error.response?.data?.message || "Failed to initiate bank setup");
    } finally {
      setSubmitting(false);
    }
  };

  const getColumns = () => {
    const baseColumns = [
      {
        title: "Account Holder",
        dataIndex: "account_holder_name",
        key: "name",
        render: (text) => <Text strong>{text || "N/A"}</Text>,
      },
    ];

    if (!isPayoutRole) {
      baseColumns.push(
        {
          title: "Bank Name",
          dataIndex: "bank_name",
          key: "bank",
          render: (text) => text || "N/A",
        },
        {
          title: "Last 4",
          dataIndex: "last4",
          key: "last4",
          render: (text) => text ? `**** ${text}` : "N/A",
        }
      );
    }

    baseColumns.push({
      title: "Type",
      dataIndex: "account_type",
      key: "type",
      render: (text) => <Tag style={{ textTransform: "capitalize" }}>{text}</Tag>,
    });

    baseColumns.push({
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const config = {
          verified: { color: "success", icon: <CheckCircleFilled /> },
          pending: { color: "warning", icon: <ClockCircleFilled /> },
          restricted: { color: "error", icon: <CloseCircleFilled /> },
          rejected: { color: "error", icon: <CloseCircleFilled /> },
          failed: { color: "error", icon: <CloseCircleFilled /> },
        }[status] || { color: "default", icon: <ClockCircleFilled /> };

        return (
          <Tag color={config.color} icon={config.icon} style={{ padding: "0 8px", borderRadius: "4px" }}>
            {status?.toUpperCase()}
          </Tag>
        );
      },
    });

    if (!isPayoutRole) {
      baseColumns.push({
        title: "Default",
        dataIndex: "is_default",
        key: "default",
        render: (isDefault) => (isDefault ? <Tag color="blue">Default</Tag> : null),
      });
    }

    if (isPayoutRole) {
      baseColumns.push(
        {
          title: "Details",
          dataIndex: "details_submitted",
          key: "details",
          render: (val) => (val ? <Tag color="blue">Submitted</Tag> : <Tag>Incomplete</Tag>),
        },
        {
          title: "Payouts",
          dataIndex: "payouts_enabled",
          key: "payouts",
          render: (val) => (val ? <Tag color="green">Enabled</Tag> : <Tag>Disabled</Tag>),
        },
        {
          title: "Requirements Due",
          dataIndex: "requirements_due",
          key: "requirements",
          render: (reqs) => {
            try {
              const items = JSON.parse(reqs || "[]");
              if (!items.length) return <Text type="secondary">None</Text>;
              return (
                <Space size={[0, 4]} wrap>
                  {items.map((item) => (
                    <Tag color="red" key={item}>
                      {item.replace(/_/g, " ")}
                    </Tag>
                  ))}
                </Space>
              );
            } catch (e) {
              return <Text type="secondary">None</Text>;
            }
          },
        }
      );
    }

    baseColumns.push({
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          {isPayoutRole && (
            <Button
              type="text"
              icon={<ReloadOutlined spin={refreshingId === record.id} />}
              onClick={() => refreshPayoutStatus(record.id)}
              disabled={refreshingId === record.id}
            >
              Refresh
            </Button>
          )}
          <Popconfirm
            title="Remove bank account?"
            description="Are you sure you want to delete this bank account?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    });

    return baseColumns;
  };

  return (
    <div style={{ padding: "16px 0" }}>
      <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Title level={5} style={{ margin: 0 }}>
          {isPayoutRole ? "Payout Settings" : "Bank Accounts (ACH)"}
        </Title>
        <Button
          type="primary"
          icon={<BankOutlined />}
          onClick={() => setModalVisible(true)}
          style={{ borderRadius: "6px", backgroundColor: "#0474ba", borderColor: "#0474ba" }}
        >
          {isPayoutRole ? "Connect Bank Account" : "Add Bank Account"}
        </Button>
      </div>

      <Table
        columns={getColumns()}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={false}
        scroll={{ x: true }}
        locale={{ emptyText: <Empty description="No bank accounts connected" /> }}
      />

      {/* Main Bank Account Form Modal */}
      <Modal
        title={
          <Space>
            <BankOutlined />
            <span>{isPayoutRole ? "Connect Bank Account" : "Add Bank Account"}</span>
          </Space>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        destroyOnClose
        zIndex={2000}
      >
        <Form
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ account_type: "personal" }}
        >
          <Paragraph>
            {isPayoutRole
              ? "Please set up your bank account to receive payments."
              : "Please set up your bank account to make payments."}
          </Paragraph>
          <Divider />

          <Form.Item
            name="account_holder_name"
            label="Account Holder Name"
            rules={[{ required: true, message: "Please enter account holder name" }]}
          >
            <Input size="large" placeholder="Enter Full Name" />
          </Form.Item>

          <Form.Item
            name="account_type"
            label="Account Type"
            rules={[{ required: true, message: "Please select account type" }]}
          >
            <Select size="large">
              <Option value="personal">Personal</Option>
              <Option value="business">Business</Option>
            </Select>
          </Form.Item>

          {!isPayoutRole && (
            <>
              <Form.Item
                name="routing_number"
                label="Routing Number"
                rules={[
                  { required: true, message: "Please enter routing number" },
                  { pattern: /^\d{9}$/, message: "Routing number must be 9 digits" }
                ]}
              >
                <Input size="large" placeholder="123456789" maxLength={9} />
              </Form.Item>

              <Form.Item
                name="account_number"
                label="Account Number"
                rules={[{ required: true, message: "Please enter account number" }]}
              >
                <Input size="large" placeholder="Account Number" />
              </Form.Item>
            </>
          )}

          <div style={{ marginTop: "32px" }}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={submitting}
              style={{ height: "48px", borderRadius: "8px" }}
            >
              {isPayoutRole ? "Setup Payout Account" : "Verify & Save Bank Account"}
            </Button>
          </div>
          <Text type="secondary" style={{ fontSize: "12px", display: "block", textAlign: "center", marginTop: "12px" }}>
            {isPayoutRole
              ? "You will be redirected to Stripe to securely provide your bank details."
              : "Your information is securely handled by Stripe. This process will verify your account."}
          </Text>
        </Form>
      </Modal>

      {/* Manual Verification Modal (for New Tab Flow) */}
      <Modal
        title="Complete Bank Verification"
        open={verificationModalVisible}
        onCancel={() => setVerificationModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setVerificationModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={savingAccount}
            onClick={() => saveBankAccountToBackend(pendingAccount)}
          >
            Finish Setup
          </Button>,
        ]}
        maskClosable={false}
        closable={false}
        zIndex={2000}
      >
        <Space direction="vertical" size="middle">
          <Paragraph>
            Your bank details have been processed successfully by Stripe.
          </Paragraph>
          <Paragraph>
            Please click "Finish Setup" below to complete the verification and add your bank account.
          </Paragraph>
        </Space>
      </Modal>
    </div>
  );
};

export default BankAccountManager;
